import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { PasswordGateSession, getPasswordGateSession, loginPasswordGate } from './passwordGate.service'

type PasswordGateProps = {
  children: React.ReactNode
}

const PASSWORD_GATE_QUERY_KEY = ['password-gate-session']

export const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
  const isGateDisabled = process.env.REACT_APP_DISABLE_PASSWORD_GATE === 'true'
  const [isGateConfigured, setIsGateConfigured] = useState(true)
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const queryClient = useQueryClient()

  const passwordGateSessionQuery = useQuery(PASSWORD_GATE_QUERY_KEY, getPasswordGateSession, {
    enabled: !isGateDisabled,
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setIsGateConfigured(data.configured !== false)
    },
  })

  const passwordGateLoginMutation = useMutation(loginPasswordGate, {
    onSuccess: () => {
      setPassword('')
      setErrorMessage('')
      setIsGateConfigured(true)
      queryClient.setQueryData<PasswordGateSession>(PASSWORD_GATE_QUERY_KEY, {
        authenticated: true,
        configured: true,
      })
    },
    onError: (error: Error) => {
      if (error.message === 'Password gate is not configured') {
        setIsGateConfigured(false)
      }
      setErrorMessage(error.message || 'Incorrect password')
    },
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage('')
    passwordGateLoginMutation.mutate(password)
  }

  const isAuthenticated = Boolean(passwordGateSessionQuery.data?.authenticated)

  if (isGateDisabled) {
    return <>{children}</>
  }

  if (isAuthenticated) {
    return <>{children}</>
  }

  if (passwordGateSessionQuery.isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
          color: '#111',
        }}
      >
        Loading...
      </div>
    )
  }

  const message =
    errorMessage ||
    (passwordGateSessionQuery.isError ? 'Unable to validate password gate right now' : '') ||
    (!isGateConfigured ? 'Password gate is not configured' : '')

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
      }}
    >
      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 340, padding: '0 20px' }}>
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Password"
          autoFocus
          autoComplete="current-password"
          required
          style={{
            width: '100%',
            border: '1px solid #d0d0d0',
            borderRadius: 6,
            padding: '10px 12px',
            marginBottom: 8,
          }}
        />
        <button
          type="submit"
          disabled={passwordGateLoginMutation.isLoading}
          style={{
            width: '100%',
            border: 'none',
            borderRadius: 6,
            padding: '10px 12px',
            background: '#111',
            color: '#fff',
            cursor: passwordGateLoginMutation.isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {passwordGateLoginMutation.isLoading ? 'Checking...' : 'Enter'}
        </button>
        <p
          aria-live="polite"
          style={{
            marginTop: 10,
            minHeight: 36,
            fontSize: 13,
            lineHeight: '18px',
            color: '#b42318',
            textAlign: 'center',
            visibility: message ? 'visible' : 'hidden',
          }}
        >
          {message || ' '}
        </p>
      </form>
    </div>
  )
}
